import {
  Add as AddIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  LocalOffer as TagIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as DropDownIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Tooltip
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { AppDispatch, RootState } from 'app/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  createTemplate,
  crudCategories,
  crudSubcategories,
  updateTemplateById
} from 'slices/payrolslices/documentTemplateSlice';
import fieldTypes from '../SignatureFields';
import { Category, SignatureField, UploadedFile } from '../types';
import { IoArrowBackCircleOutline } from 'react-icons/io5';
import ConfirmationDialog from 'commonComponents/ConfirmationDialog';

// Green theme styles
const greenButtonSx = {
  backgroundColor: '#217346',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1a5d37'
  },
  '&:disabled': {
    backgroundColor: '#c8e6c9',
    color: '#81c784'
  }
};

const greenOptionSx = {
  color: '#217346',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#e8f5e9',
    color: '#1a5d37'
  }
};

interface TemplateConfigStepProps {
  documentNameState: string;
  setDocumentNameState: (name: string) => void;
  category: string;
  setCategory: (category: string) => void;
  subCategory: string;
  setSubCategory: (subCategory: string) => void;
  uploadedFile: UploadedFile | null;
  signatureFields: SignatureField[];
  fieldRequirements: Record<string, boolean>;
  availableCategories: Category[];
  isEditMode: boolean;
  onBack: () => void;
  onSave?: () => void;
}

const TemplateConfigStep: React.FC<TemplateConfigStepProps> = ({
  documentNameState,
  setDocumentNameState,
  category,
  setCategory,
  subCategory,
  setSubCategory,
  uploadedFile,
  signatureFields,
  fieldRequirements,
  availableCategories,
  isEditMode,
  onBack,
  onSave
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTinyScreen = useMediaQuery('(max-width:400px)');

  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const {
    categoryLoading,
    categoryError,
    subcategoryLoading,
    subcategoryError,
    availableSubcategories,
    templateCreating
  } = useSelector((state: RootState) => state.documentTemplate);

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const { documentId, documentName, selectedCategory } = location.state || {};

  // **RESPONSIVE TYPOGRAPHY SIZES**
  const getResponsiveSizes = () => {
    if (isTinyScreen) {
      return {
        caption: '0.625rem', // 10px
        body: '0.6875rem', // 11px
        button: '0.6875rem', // 11px
        title: '0.875rem', // 14px
        cardTitle: '1rem', // 16px
        iconSize: 16,
        spacing: 1,
        padding: 1
      };
    } else if (isSmallMobile) {
      return {
        caption: '0.6875rem', // 11px
        body: '0.75rem', // 12px
        button: '0.75rem', // 12px
        title: '0.9375rem', // 15px
        cardTitle: '1.0625rem', // 17px
        iconSize: 18,
        spacing: 1.5,
        padding: 1.5
      };
    } else if (isMobile) {
      return {
        caption: '0.75rem', // 12px
        body: '0.8125rem', // 13px
        button: '0.8125rem', // 13px
        title: '1rem', // 16px
        cardTitle: '1.125rem', // 18px
        iconSize: 20,
        spacing: 2,
        padding: 2
      };
    } else {
      return {
        caption: '0.875rem', // 14px
        body: '0.9375rem', // 15px
        button: '0.9375rem', // 15px
        title: '1.125rem', // 18px
        cardTitle: '1.25rem', // 20px
        iconSize: 24,
        spacing: 3,
        padding: 3
      };
    }
  };

  const sizes = getResponsiveSizes();

  // Dialog states
  const [showCreateCategoryDialog, setShowCreateCategoryDialog] = useState(false);
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [showCreateSubCategoryDialog, setShowCreateSubCategoryDialog] = useState(false);
  const [showEditSubCategoryDialog, setShowEditSubCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const [editSubCategoryName, setEditSubCategoryName] = useState('');
  const [subCategoryToEdit, setSubCategoryToEdit] = useState('');

  // Confirmation dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string>('');
  const [entityToDelete, setEntityToDelete] = useState<Category | string | null>(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  // Select open states
  const [categorySelectOpen, setCategorySelectOpen] = useState(false);
  const [subcategorySelectOpen, setSubcategorySelectOpen] = useState(false);

  // Helper functions for confirmation dialog
  const getConfirmDialogTitle = () => {
    switch (confirmAction) {
      case 'deleteCategory':
        return 'Delete Category';
      case 'deleteSubcategory':
        return 'Delete Subcategory';
      default:
        return 'Confirm Action';
    }
  };

  const getConfirmDialogMessage = () => {
    switch (confirmAction) {
      case 'deleteCategory':
        const categoryName = (entityToDelete as Category)?.category;
        return `Are you sure you want to delete the category "${categoryName}"? This action cannot be undone and may affect existing templates.`;
      case 'deleteSubcategory':
        return `Are you sure you want to delete the subcategory "${entityToDelete}"? This action cannot be undone and may affect existing templates.`;
      default:
        return 'Are you sure you want to perform this action?';
    }
  };

  const getConfirmDialogButtonText = () => {
    switch (confirmAction) {
      case 'deleteCategory':
        return 'Delete Category';
      case 'deleteSubcategory':
        return 'Delete Subcategory';
      default:
        return 'Confirm';
    }
  };

  // Confirmation dialog handlers
  const handleDeleteCategoryConfirm = (categoryObj: Category) => {
    setEntityToDelete(categoryObj);
    setConfirmAction('deleteCategory');
    setConfirmDialogOpen(true);
  };

  const handleDeleteSubcategoryConfirm = (subcategoryName: string) => {
    setEntityToDelete(subcategoryName);
    setConfirmAction('deleteSubcategory');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    setConfirmDeleteLoading(true);

    try {
      switch (confirmAction) {
        case 'deleteCategory':
          const categoryToDelete = entityToDelete as Category;
          await dispatch(
            crudCategories({
              group_id: selectedProjectId || '',
              service_id: selectedServiceTitle || '',
              action: 'delete',
              category_id: categoryToDelete.category_id
            })
          ).unwrap();
          refreshCategories();
          if (category === categoryToDelete.category) {
            setCategory('');
            setSubCategory('');
          }
          break;

        case 'deleteSubcategory':
          const subcategoryName = entityToDelete as string;
          const subcategoryObj = availableSubcategories.find(
            (sc) => sc.subcategory === subcategoryName
          );
          if (subcategoryObj) {
            await dispatch(
              crudSubcategories({
                group_id: selectedProjectId || '',
                service_id: selectedServiceTitle || '',
                action: 'delete',
                subcategory_id: subcategoryObj.subcategory_id
              })
            ).unwrap();
            refreshSubcategories();
            if (subCategory === subcategoryName) {
              setSubCategory('');
            }
          }
          break;
      }

      // Simulate async operation delay
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Error performing delete action:', error);
    } finally {
      setConfirmDeleteLoading(false);
      setConfirmDialogOpen(false);
      setConfirmAction('');
      setEntityToDelete(null);
    }
  };

  const handleCloseConfirmDialog = () => {
    if (!confirmDeleteLoading) {
      setConfirmDialogOpen(false);
      setConfirmAction('');
      setEntityToDelete(null);
    }
  };

  // Helper functions
  const refreshCategories = () => {
    dispatch(
      crudCategories({
        group_id: selectedProjectId || '',
        service_id: selectedServiceTitle || '',
        action: 'get'
      })
    );
  };

  const refreshSubcategories = () => {
    dispatch(
      crudSubcategories({
        group_id: selectedProjectId || '',
        service_id: selectedServiceTitle || '',
        action: 'get'
      })
    );
  };

  const getCategoryIdFromName = (categoryName: string): string => {
    const categoryObj = availableCategories.find((cat) => cat.category === categoryName);
    return categoryObj?.category_id || '';
  };

  const getSubcategoryIdFromName = (subcategoryName: string): string => {
    const subcategoryObj = availableSubcategories.find(
      (subcat) => subcat.subcategory === subcategoryName
    );
    return subcategoryObj?.subcategory_id || '';
  };

  // Load subcategories when category changes
  useEffect(() => {
    if (category) {
      refreshSubcategories();
      setSubCategory('');
    }
  }, [category, dispatch, selectedProjectId, selectedServiceTitle]);

  // CRUD handlers (keeping original logic)
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await dispatch(
        crudCategories({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          action: 'create',
          category: newCategoryName.trim()
        })
      ).unwrap();
      refreshCategories();
      setCategory(newCategoryName.trim());
      setShowCreateCategoryDialog(false);
      setNewCategoryName('');
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleEditCategory = async () => {
    if (!editCategoryName.trim() || !categoryToEdit) return;
    try {
      await dispatch(
        crudCategories({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          action: 'update',
          category_id: categoryToEdit.category_id,
          category: editCategoryName.trim()
        })
      ).unwrap();
      refreshCategories();
      if (category === categoryToEdit.category) {
        setCategory(editCategoryName.trim());
      }
      setShowEditCategoryDialog(false);
      setEditCategoryName('');
      setCategoryToEdit(null);
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleCreateSubCategory = async () => {
    if (!newSubCategoryName.trim()) return;
    try {
      await dispatch(
        crudSubcategories({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          action: 'create',
          subcategory: newSubCategoryName.trim()
        })
      ).unwrap();
      refreshSubcategories();
      setSubCategory(newSubCategoryName.trim());
      setShowCreateSubCategoryDialog(false);
      setNewSubCategoryName('');
    } catch (error) {
      console.error('Error creating subcategory:', error);
    }
  };

  const handleEditSubCategory = async () => {
    if (!editSubCategoryName.trim() || !subCategoryToEdit) return;
    try {
      const subcategoryObj = availableSubcategories.find(
        (sc) => sc.subcategory === subCategoryToEdit
      );
      if (!subcategoryObj) return;
      await dispatch(
        crudSubcategories({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          action: 'update',
          subcategory_id: subcategoryObj.subcategory_id,
          subcategory: editSubCategoryName.trim()
        })
      ).unwrap();
      refreshSubcategories();
      if (subCategory === subCategoryToEdit) {
        setSubCategory(editSubCategoryName.trim());
      }
      setShowEditSubCategoryDialog(false);
      setEditSubCategoryName('');
      setSubCategoryToEdit('');
    } catch (error) {
      console.error('Error updating subcategory:', error);
    }
  };

  const handleCategoryChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setShowCreateCategoryDialog(true);
    } else {
      setCategory(value);
      setSubCategory('');
    }
  };

  const handleSubCategoryChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setShowCreateSubCategoryDialog(true);
    } else {
      setSubCategory(value);
    }
  };

  const getImageFileType = (file: File): string => {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const mimeTypeMap: Record<string, string> = {
      jpg: 'jpg',
      jpeg: 'jpeg',
      png: 'png',
      gif: 'gif',
      webp: 'webp'
    };
    return mimeTypeMap[extension] || 'jpg';
  };

  const handleSave = async () => {
    if (onSave) {
      onSave();
    }

    const getFieldKeyFromField = (field: SignatureField) => {
      const config = fieldTypes.find((f) => f.label === field.fieldLabel);

      if (!config) {
        return 'unknown';
      }

      // Use the fieldKey from config, but apply specific mappings
      const fieldKey = config.fieldKey;

      // **Map specific field keys to their proper types**
      const fieldTypeMapping: { [key: string]: string } = {
        signature: 'signature', // Signature field
        initials: 'text', // Initials maps to text
        expiry: 'date', // Expiry maps to date
        date: 'date', // Date stays as date
        checkbox: 'boolean' // Checkbox maps to boolean
      };

      // Return mapped type if exists, otherwise return the original fieldKey
      return fieldTypeMapping[fieldKey] || fieldKey;
    };

    const categoryId = getCategoryIdFromName(category);
    const subcategoryId = subCategory ? getSubcategoryIdFromName(subCategory) : undefined;

    const payload = {
      group_id: selectedProjectId || '',
      service_id: selectedServiceTitle || '',
      documentName: documentNameState,
      documentDate: new Date().toISOString().split('T')[0],
      category_id: categoryId,
      subcategory_id: subcategoryId,
      inputFields: signatureFields.map((field) => ({
        id: field.id,
        type: getFieldKeyFromField(field), // This will now return 'signature' for signature fields
        fieldLabel: field.fieldLabel || 'Unknown',
        x: Math.round(field.x),
        y: Math.round(field.y),
        xPercent: field.xPercent,
        yPercent: field.yPercent,
        widthPercent: field.widthPercent,
        heightPercent: field.heightPercent,
        pageNumber: field.pageNumber,
        isRequired: fieldRequirements[field.id] || false,
        value: field.value || ''
      })),
      // Handle empty uploadedFile
      uploadedFile:
        uploadedFile && uploadedFile.file
          ? {
              fileName: uploadedFile.file.name,
              fileSize: uploadedFile.file.size,
              fileType:
                uploadedFile.type === 'image'
                  ? getImageFileType(uploadedFile.file)
                  : uploadedFile.type || 'unknown',
              originalFileName: uploadedFile.file.name,
              storedFilePath: uploadedFile.base64?.split(',')[1] || ''
            }
          : {}
    };

    console.log('Payload for saving template:', payload);

    try {
      if (isEditMode) {
        await dispatch(
          updateTemplateById({
            ...payload,
            document_id: documentId || 'current-doc-id',
            category: category
          })
        ).unwrap();
      } else {
        await dispatch(createTemplate(payload)).unwrap();
      }
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  return (
    <Box sx={{ p: { xs: sizes.padding / 2, sm: sizes.padding, md: sizes.padding * 1.5 } }}>
      <Grid container spacing={sizes.spacing}>
        {/* Configuration Form */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ borderRadius: 2, overflow: 'visible' }}>
            <CardHeader
              title="Template Details"
              sx={{
                pb: sizes.padding / 2,
                '& .MuiCardHeader-title': {
                  fontSize: sizes.cardTitle,
                  fontWeight: 600
                }
              }}
            />
            <CardContent sx={{ pt: 0, p: sizes.padding }}>
              <Stack spacing={sizes.spacing}>
                {/* Template Name */}
                <TextField
                  label="Template Name"
                  value={documentNameState}
                  onChange={(e) => setDocumentNameState(e.target.value)}
                  fullWidth
                  required
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: sizes.body,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: sizes.caption,
                      '&.Mui-focused': {
                        color: '#217346'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: sizes.body,
                      padding: isTinyScreen ? '8px 12px' : isMobile ? '10px 14px' : '16.5px 14px'
                    }
                  }}
                  InputProps={{
                    endAdornment: documentNameState && (
                      <IconButton
                        size="small"
                        onClick={() => setDocumentNameState('')}
                        edge="end"
                        sx={{ mr: -0.5, p: 0.5 }}
                      >
                        <CloseIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                      </IconButton>
                    )
                  }}
                />

                {/* Category Selection */}
                <FormControl
                  fullWidth
                  required
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <InputLabel
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: sizes.caption,
                      '& svg': { fontSize: sizes.iconSize },
                      '&.Mui-focused': { color: '#217346' }
                    }}
                  >
                    <CategoryIcon fontSize="inherit" />
                    Category
                  </InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={handleCategoryChange}
                    open={categorySelectOpen}
                    onOpen={() => setCategorySelectOpen(true)}
                    onClose={() => setCategorySelectOpen(false)}
                    sx={{
                      borderRadius: 2,
                      fontSize: sizes.body,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: sizes.body,
                        padding: isTinyScreen ? '8px 12px' : isMobile ? '10px 14px' : '16.5px 14px'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      }
                    }}
                    IconComponent={DropDownIcon}
                    endAdornment={
                      category && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategory('');
                            setSubCategory('');
                          }}
                          sx={{ position: 'absolute', right: 32, zIndex: 10, p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      <Typography color="text.secondary" sx={{ fontSize: sizes.caption }}>
                        Select a category
                      </Typography>
                    </MenuItem>
                    {availableCategories.map((categoryObj) => (
                      <MenuItem
                        key={categoryObj.category_id}
                        value={categoryObj.category}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: isTinyScreen ? 0.5 : 1,
                          minHeight: isTinyScreen ? 32 : 40
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <CategoryIcon sx={{ fontSize: sizes.iconSize, color: '#217346' }} />
                          <Typography sx={{ fontSize: sizes.body }}>
                            {categoryObj.category}
                          </Typography>
                        </Box>
                        <Fade in={categorySelectOpen}>
                          <Box>
                            <Tooltip title="Edit Category">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCategoryToEdit(categoryObj);
                                  setEditCategoryName(categoryObj.category);
                                  setShowEditCategoryDialog(true);
                                }}
                                sx={{ p: 0.25, ml: 0.5, minWidth: 'auto' }}
                              >
                                <EditIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Category">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCategoryConfirm(categoryObj);
                                }}
                                sx={{ p: 0.25, minWidth: 'auto' }}
                              >
                                <DeleteIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Fade>
                      </MenuItem>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem value="add_new" sx={{ ...greenOptionSx, fontSize: sizes.body }}>
                      <AddIcon sx={{ fontSize: sizes.iconSize, mr: 1 }} />
                      Create New Category
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Subcategory Selection */}
                <FormControl
                  fullWidth
                  variant="outlined"
                  disabled={!category}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <InputLabel
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: sizes.caption,
                      '& svg': { fontSize: sizes.iconSize },
                      '&.Mui-focused': { color: '#217346' }
                    }}
                  >
                    <TagIcon fontSize="inherit" />
                    Subcategory (Optional)
                  </InputLabel>
                  <Select
                    value={subCategory}
                    label="Subcategory (Optional)"
                    onChange={handleSubCategoryChange}
                    open={subcategorySelectOpen}
                    onOpen={() => setSubcategorySelectOpen(true)}
                    onClose={() => setSubcategorySelectOpen(false)}
                    sx={{
                      borderRadius: 2,
                      fontSize: sizes.body,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: sizes.body,
                        padding: isTinyScreen ? '8px 12px' : isMobile ? '10px 14px' : '16.5px 14px'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#217346'
                      }
                    }}
                    IconComponent={DropDownIcon}
                    endAdornment={
                      subCategory && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSubCategory('');
                          }}
                          sx={{ position: 'absolute', right: 32, zIndex: 10, p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      <Typography color="text.secondary" sx={{ fontSize: sizes.caption }}>
                        Select a subcategory
                      </Typography>
                    </MenuItem>
                    {availableSubcategories.map((subcat) => (
                      <MenuItem
                        key={subcat.subcategory_id}
                        value={subcat.subcategory}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: isTinyScreen ? 0.5 : 1,
                          minHeight: isTinyScreen ? 32 : 40
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <TagIcon sx={{ fontSize: sizes.iconSize, color: '#217346' }} />
                          <Typography sx={{ fontSize: sizes.body }}>
                            {subcat.subcategory}
                          </Typography>
                        </Box>
                        <Fade in={subcategorySelectOpen}>
                          <Box>
                            <Tooltip title="Edit Subcategory">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSubCategoryToEdit(subcat.subcategory);
                                  setEditSubCategoryName(subcat.subcategory);
                                  setShowEditSubCategoryDialog(true);
                                }}
                                sx={{ p: 0.25, ml: 0.5, minWidth: 'auto' }}
                              >
                                <EditIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Subcategory">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSubcategoryConfirm(subcat.subcategory);
                                }}
                                sx={{ p: 0.25, minWidth: 'auto' }}
                              >
                                <DeleteIcon sx={{ fontSize: sizes.iconSize - 4 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Fade>
                      </MenuItem>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem value="add_new" sx={{ ...greenOptionSx, fontSize: sizes.body }}>
                      <AddIcon sx={{ fontSize: sizes.iconSize, mr: 1 }} />
                      Create New Subcategory
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Panel */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#f1f8e9' }}>
            <CardHeader
              title="Template Summary"
              sx={{
                pb: sizes.padding / 2,
                '& .MuiCardHeader-title': {
                  fontSize: sizes.title,
                  fontWeight: 600,
                  color: '#217346'
                }
              }}
            />
            <CardContent sx={{ pt: 0, p: sizes.padding }}>
              <Stack spacing={sizes.spacing}>
                {/* Document Info */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: '#217346',
                      fontSize: sizes.caption,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    DOCUMENT
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 500,
                      fontSize: sizes.body
                    }}
                  >
                    {uploadedFile?.file?.name || 'No document uploaded'}
                  </Typography>
                  {uploadedFile?.file?.size && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: sizes.caption }}
                    >
                      {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB •{' '}
                      {uploadedFile.type?.toUpperCase()}
                    </Typography>
                  )}
                </Box>

                {/* Fields Info */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: '#217346',
                      fontSize: sizes.caption,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    FORM FIELDS
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 500,
                      fontSize: sizes.body
                    }}
                  >
                    {signatureFields.length} field{signatureFields.length !== 1 ? 's' : ''}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: sizes.caption }}
                  >
                    {Object.values(fieldRequirements).filter(Boolean).length} required •{' '}
                    {signatureFields.length -
                      Object.values(fieldRequirements).filter(Boolean).length}{' '}
                    optional
                  </Typography>
                </Box>

                {/* Category Info */}
                {category && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: '#217346',
                        fontSize: sizes.caption,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      CATEGORY
                    </Typography>
                    <Chip
                      label={category}
                      size="small"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        width: 'fit-content',
                        bgcolor: '#217346',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: sizes.caption,
                        height: isTinyScreen ? 24 : 28
                      }}
                    />
                  </Box>
                )}

                {/* Subcategory Info */}
                {subCategory && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: '#217346',
                        fontSize: sizes.caption,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      SUBCATEGORY
                    </Typography>
                    <Chip
                      label={subCategory}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        width: 'fit-content',
                        borderColor: '#217346',
                        color: '#217346',
                        fontWeight: 500,
                        fontSize: sizes.caption,
                        height: isTinyScreen ? 24 : 28
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: sizes.spacing,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Button
          onClick={onBack}
          sx={{
            color: '#666',
            fontSize: sizes.button,
            minHeight: isTinyScreen ? 28 : isMobile ? 32 : 36
          }}
          startIcon={<IoArrowBackCircleOutline size={sizes.iconSize} />}
          size={isMobile ? 'small' : 'medium'}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!documentNameState || !category || templateCreating}
          startIcon={
            templateCreating ? (
              <CircularProgress size={sizes.iconSize - 4} />
            ) : (
              <SaveIcon sx={{ fontSize: sizes.iconSize }} />
            )
          }
          sx={{
            ...greenButtonSx,
            fontSize: sizes.button,
            minHeight: isTinyScreen ? 28 : isMobile ? 32 : 36,
            minWidth: isTinyScreen ? 80 : isMobile ? 100 : 120
          }}
          size={isMobile ? 'small' : 'medium'}
        >
          {templateCreating
            ? isEditMode
              ? 'Updating...'
              : 'Creating...'
            : isEditMode
            ? 'Update Template'
            : 'Create Template'}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        title={getConfirmDialogTitle()}
        message={getConfirmDialogMessage()}
        confirmButtonText={getConfirmDialogButtonText()}
        confirmButtonColor="error"
        onConfirm={handleConfirmAction}
        onCancel={handleCloseConfirmDialog}
        loading={confirmDeleteLoading}
      />

      {/* Create/Edit Dialog Components with responsive styling */}
      <Dialog
        open={showCreateCategoryDialog}
        onClose={() => setShowCreateCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isTinyScreen}
        PaperProps={{ sx: { borderRadius: isTinyScreen ? 0 : 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, color: '#217346', fontSize: sizes.title }}>
          Create New Category
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            error={!!categoryError}
            helperText={categoryError}
            sx={{
              mt: sizes.spacing,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: sizes.body
              },
              '& .MuiInputLabel-root': {
                fontSize: sizes.caption,
                '&.Mui-focused': { color: '#217346' }
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#217346'
              },
              '& .MuiFormHelperText-root': {
                fontSize: sizes.caption
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: sizes.padding, pt: 1 }}>
          <Button
            onClick={() => setShowCreateCategoryDialog(false)}
            color="inherit"
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: sizes.button }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            disabled={!newCategoryName.trim() || categoryLoading}
            startIcon={
              categoryLoading ? (
                <CircularProgress size={sizes.iconSize - 4} />
              ) : (
                <AddIcon sx={{ fontSize: sizes.iconSize }} />
              )
            }
            sx={{ ...greenButtonSx, fontSize: sizes.button }}
            size={isMobile ? 'small' : 'medium'}
          >
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog
        open={showEditCategoryDialog}
        onClose={() => setShowEditCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isTinyScreen}
        PaperProps={{ sx: { borderRadius: isTinyScreen ? 0 : 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, color: '#217346', fontSize: sizes.title }}>
          Edit Category
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            value={editCategoryName}
            onChange={(e) => setEditCategoryName(e.target.value)}
            error={!!categoryError}
            helperText={categoryError}
            sx={{
              mt: sizes.spacing,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: sizes.body
              },
              '& .MuiInputLabel-root': {
                fontSize: sizes.caption,
                '&.Mui-focused': { color: '#217346' }
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#217346'
              },
              '& .MuiFormHelperText-root': {
                fontSize: sizes.caption
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: sizes.padding, pt: 1 }}>
          <Button
            onClick={() => setShowEditCategoryDialog(false)}
            color="inherit"
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: sizes.button }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditCategory}
            variant="contained"
            disabled={!editCategoryName.trim() || categoryLoading}
            startIcon={
              categoryLoading ? (
                <CircularProgress size={sizes.iconSize - 4} />
              ) : (
                <EditIcon sx={{ fontSize: sizes.iconSize }} />
              )
            }
            sx={{ ...greenButtonSx, fontSize: sizes.button }}
            size={isMobile ? 'small' : 'medium'}
          >
            Update Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create SubCategory Dialog */}
      <Dialog
        open={showCreateSubCategoryDialog}
        onClose={() => setShowCreateSubCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isTinyScreen}
        PaperProps={{ sx: { borderRadius: isTinyScreen ? 0 : 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, color: '#217346', fontSize: sizes.title }}>
          Create New Subcategory
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subcategory Name"
            fullWidth
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            value={newSubCategoryName}
            onChange={(e) => setNewSubCategoryName(e.target.value)}
            error={!!subcategoryError}
            helperText={
              subcategoryError || (category && `Will be created under "${category}" category`)
            }
            sx={{
              mt: sizes.spacing,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: sizes.body
              },
              '& .MuiInputLabel-root': {
                fontSize: sizes.caption,
                '&.Mui-focused': { color: '#217346' }
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#217346'
              },
              '& .MuiFormHelperText-root': {
                fontSize: sizes.caption
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: sizes.padding, pt: 1 }}>
          <Button
            onClick={() => setShowCreateSubCategoryDialog(false)}
            color="inherit"
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: sizes.button }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSubCategory}
            variant="contained"
            disabled={!newSubCategoryName.trim() || subcategoryLoading}
            startIcon={
              subcategoryLoading ? (
                <CircularProgress size={sizes.iconSize - 4} />
              ) : (
                <AddIcon sx={{ fontSize: sizes.iconSize }} />
              )
            }
            sx={{ ...greenButtonSx, fontSize: sizes.button }}
            size={isMobile ? 'small' : 'medium'}
          >
            Create Subcategory
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit SubCategory Dialog */}
      <Dialog
        open={showEditSubCategoryDialog}
        onClose={() => setShowEditSubCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isTinyScreen}
        PaperProps={{ sx: { borderRadius: isTinyScreen ? 0 : 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, color: '#217346', fontSize: sizes.title }}>
          Edit Subcategory
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subcategory Name"
            fullWidth
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            value={editSubCategoryName}
            onChange={(e) => setEditSubCategoryName(e.target.value)}
            error={!!subcategoryError}
            helperText={subcategoryError}
            sx={{
              mt: sizes.spacing,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: sizes.body
              },
              '& .MuiInputLabel-root': {
                fontSize: sizes.caption,
                '&.Mui-focused': { color: '#217346' }
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#217346'
              },
              '& .MuiFormHelperText-root': {
                fontSize: sizes.caption
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: sizes.padding, pt: 1 }}>
          <Button
            onClick={() => setShowEditSubCategoryDialog(false)}
            color="inherit"
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: sizes.button }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubCategory}
            variant="contained"
            disabled={!editSubCategoryName.trim() || subcategoryLoading}
            startIcon={
              subcategoryLoading ? (
                <CircularProgress size={sizes.iconSize - 4} />
              ) : (
                <EditIcon sx={{ fontSize: sizes.iconSize }} />
              )
            }
            sx={{ ...greenButtonSx, fontSize: sizes.button }}
            size={isMobile ? 'small' : 'medium'}
          >
            Update Subcategory
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateConfigStep;
